/**
 * 头像地址解析。
 *
 * <p>头像字段在不同时期存过两种形态：
 * - 完整 URL（http/https/data:）—— 历史数据，直接使用；
 * - 附件 fileId —— 上传头像后落库的形态，展示时需换取预签名 URL。
 *
 * <p>之所以不把预签名 URL 直接写进 avatar 字段：预签名有有效期，过期后头像会整片裂图。
 * 存 fileId 则由本模块在展示时换取，并在内存中缓存，同一会话内不重复请求。
 */

import { getFileInlineUrl } from '@/api/container';

/** fileId → 解析后的可展示 URL（会话内缓存，避免 TopBar 等高频组件重复请求） */
const urlCache = new Map<string, { url: string; expiresAt: number }>();

/** 是否已是可直接展示的 URL / data URI */
function isDirectUrl(value: string): boolean {
  return /^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:');
}

/**
 * 从预签名 URL 解析出失效时间戳（毫秒）。非 S3 签名 URL 或解析失败返回 0（视为长期有效）。
 * <p>形态：{@code ...?X-Amz-Date=20261014T020000Z&X-Amz-Expires=604800}
 */
function parseExpiresAt(url: string): number {
  try {
    const search = new URL(url).searchParams;
    const date = search.get('X-Amz-Date');
    const expires = search.get('X-Amz-Expires');
    if (!date || !expires) {
      return 0;
    }
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date);
    if (!m) {
      return 0;
    }
    const start = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
    return start + Number(expires) * 1000;
  } catch {
    return 0;
  }
}

/**
 * 解析头像字段为可展示 URL。
 * @param value avatar 字段原始值（URL 或 fileId），空值返回空串
 * @returns 可直接填入 <img src> / Avatar src 的 URL；解析失败返回空串（调用方回退到姓名首字）
 */
export async function resolveAvatarUrl(value?: string | null): Promise<string> {
  const raw = (value || '').trim();
  if (!raw) {
    return '';
  }
  if (isDirectUrl(raw)) {
    return raw;
  }
  const cached = urlCache.get(raw);
  // 留有 1 分钟余量，避免刚渲染出来就过期
  if (cached && (cached.expiresAt === 0 || cached.expiresAt - Date.now() > 60_000)) {
    return cached.url;
  }
  try {
    const url = await getFileInlineUrl(raw);
    if (url) {
      urlCache.set(raw, { url, expiresAt: parseExpiresAt(url) });
      return url;
    }
  } catch {
    // 对象缺失 / 无权限 / 后端未启动 —— 静默回退到姓名首字头像
  }
  return '';
}

/** 写入缓存（上传成功、已知 fileId 与新 URL 时使用，避免多余一次换取请求） */
export function cacheAvatarUrl(fileId: string, url: string): void {
  if (fileId && url) {
    urlCache.set(fileId, { url, expiresAt: parseExpiresAt(url) });
  }
}

/** 清空缓存（切换用户 / 退出登录时调用，避免串号） */
export function clearAvatarCache(): void {
  urlCache.clear();
}

export default resolveAvatarUrl;
